'use client';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import type { z } from 'zod';
import React, { useEffect } from 'react';
import { AddCourseStepper } from './add-course-stepper';
import { AddCourseStep0 } from './steps/add-course-step-0';
import { AddCourseControls } from './add-course-controls';
import { AddCourseStepsBuckets } from './steps/add-course-steps-validator';
import { deepGet } from '@eliadmin/core/src/utils/misc-utils';
import { editCourse } from '@eliadmin/core/src/api/courses/jobportal-courses-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRouter, useSearchParams } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { PortalContentLoader } from '../../profile/portal-content-loader';
import { useProfileNavigation } from '../../profile/profile-navigation';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createCourseSchema } from './create-course-form';
import { useQuery } from '@tanstack/react-query';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { buildCourseTextFields, COURSE_URL_FIELD_ID } from '../course-external-link';
import {
  getCourseFormErrorField,
  getEditCourseValues,
  getPreservedCourseUpdateFields,
} from './course-form-values';

export const editCourseSchema = createCourseSchema;

type EditCourseFormValues = z.infer<ReturnType<typeof createCourseSchema>>;

export const EditCourseForm = () => {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(state => state.currentLocale);
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const courseSlug = searchParams.get('slug');
  const { me, isInitialized } = useCustomerStore(state => state);

  const {
    data: courseData,
    isPending,
    isError,
    refetch,
  } = useQuery({
    // Keep the full authenticated edit response separate from the public detail cache.
    queryKey: ['course-edit', courseSlug, locale, me?.customer?.id],
    queryFn: async () => {
      const response = await fetchSlugDetail({
        slug: courseSlug ?? '',
        locale,
        authToken: me?.authToken,
        cache: 'no-store',
      });
      const course = response?.['App\\Models\\Course\\Course'];
      buildCourseTextFields(course?.text_fields);
      return course;
    },
    enabled: !!courseSlug && isInitialized && !!me?.authToken,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const router = useRouter();
  const navigateToProfile = useProfileNavigation();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!me || !me.customer || me.customer.customer_type?.type !== 'school') {
      router.push('/');
    }
  }, [isInitialized, me, router]);

  const form = useForm<EditCourseFormValues>({
    mode: 'onChange',
    resolver: zodResolver(createCourseSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      image: null,
      description: '',
      content: '',
      targetAudience: '',
      confirmationForm: [],
      learningMethod: [],
      instructors: [{ name: '' }],
      courseType: [],
      categories: '',
      startDate: null,
      endDate: null,
      price: '',
      externalUrl: '',
      canBeReimbursed: false,
      requiresExam: false,
      courseLocation: '',
    } as any,
  });
  const { dirtyFields } = form.formState;

  useEffect(() => {
    if (courseData) {
      form.reset(getEditCourseValues(courseData) as EditCourseFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData]);

  const [currentStep, setCurrentStep] = React.useState(0);
  const [currentErrorStep, setCurrentErrorStep] = React.useState<
    keyof typeof AddCourseStepsBuckets | null
  >(null);

  const checkIfStepInputsValid = async (step: keyof typeof AddCourseStepsBuckets) => {
    const currentBucket = AddCourseStepsBuckets[step];
    if (!currentBucket) return true;

    const isValid = await form.trigger(currentBucket as any, {
      shouldFocus: true,
    });

    const hasErrors = currentBucket.some(field => {
      const error = deepGet(form.formState.errors, field);
      return !!error;
    });

    if (!isValid || hasErrors) {
      setCurrentErrorStep(step);
      return false;
    }
    setCurrentErrorStep(null);
    return true;
  };

  const handleSetCurrentStep = async (step: number) => {
    const isValid = await checkIfStepInputsValid(currentStep as keyof typeof AddCourseStepsBuckets);
    if (isValid) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = form.handleSubmit(
    async data => {
      let textFields: ReturnType<typeof buildCourseTextFields>;
      let preservedFields: ReturnType<typeof getPreservedCourseUpdateFields>;
      try {
        if (!courseSlug || !courseData || !me?.authToken) throw new Error('Course not loaded.');
        // Do not refetch the form query: that would reset the user's unsaved changes.
        const response = await fetchSlugDetail({
          slug: courseSlug,
          locale,
          authToken: me.authToken,
          cache: 'no-store',
        });
        const latest = response?.['App\\Models\\Course\\Course'];
        if (!latest || latest.id !== courseData.id) throw new Error('Course changed.');
        textFields = buildCourseTextFields(
          latest.text_fields,
          dirtyFields.externalUrl ? data.externalUrl : undefined,
        );
        preservedFields = getPreservedCourseUpdateFields(
          latest,
          data.categories,
          !!dirtyFields.categories,
        );
      } catch {
        toast({
          title: 'Kurz sa nepodarilo uložiť.',
          description:
            'Nepodarilo sa načítať aktuálne údaje kurzu. Vaše zmeny zostali vo formulári. Skúste uloženie znova.',
          variant: 'destructive',
        });
        return;
      }

      // Only upload if image is a File type (newly selected) or if the image field is touched
      if (data.image && (data.image instanceof File || form.formState.dirtyFields.image)) {
        const formData = new FormData();

        formData.append('file', data.image);

        try {
          const res = await uploadFile({
            authToken: me?.authToken,
            formData,
            locale,
          });
          data.image = res.data as unknown as File;
        } catch (e) {
          return;
        }
      }

      const transformedData = {
        ...preservedFields,
        name: data.name,
        description: data.description,
        short_description: data.content,
        date_start: data.startDate,
        date_stop: data.endDate,
        price_with_vat: Number(data.price.replace(',', '.')),
        text_fields: textFields,
        image: data.image,
        metadata: {
          ...preservedFields.metadata,
          instructors: data.instructors?.map(instructor => {
            if (!instructor.name) {
              return undefined;
            }
            return {
              name: instructor.name,
            };
          }),
          canBeReimbursed: data.canBeReimbursed,
          requiresExam: data.requiresExam,
          confirmationForm: data.confirmationForm.length ? data.confirmationForm : [],
          learningMethod: data.learningMethod.length ? data.learningMethod : [],
          targetAudience: data.targetAudience,
          courseType: data.courseType
            ?.map(value => {
              return {
                id: value,
                name: value,
              };
            })
            .filter(Boolean),
          agencyName: me?.customer?.name,
          courseLocation: data.courseLocation,
        },
      };

      await editCourse({
        slug: courseSlug ?? '',
        locale,
        body: transformedData,
        authToken: me?.authToken,
      })
        .then(() => {
          toast({
            title: 'Kurz bol upravený.',
            variant: 'success',
          });
          navigateToProfile(ROUTES.PROFILE() + '?initStep=school-courses');
        })
        .catch(e => {
          if (e instanceof ValidationError) {
            for (const [field, errors] of Object.entries(e.fields)) {
              const textFieldIndex = /^text_fields\.(\d+)(?:\.|$)/.exec(field)?.[1];
              const isUrlError =
                field === 'text_fields' ||
                (textFieldIndex !== undefined &&
                  textFields[Number(textFieldIndex)]?.id === COURSE_URL_FIELD_ID);
              const formField = isUrlError ? 'externalUrl' : getCourseFormErrorField(field);
              form.setError(formField as any, { type: 'manual', message: errors.join('\n') });
            }
            setCurrentStep(0);
          }
          toast({
            title: t('text1472'),
            description:
              (e instanceof ValidationError && Object.values(e.fields).flat().join(' ')) ||
              'Skontrolujte prosím zadané údaje a skúste to znova.',
            variant: 'destructive',
          });
        });
    },
    () => {},
  );

  if (!isInitialized || !me || !me.customer || me.customer.customer_type?.type !== 'school') {
    return <PortalContentLoader />;
  }

  if (!courseSlug || !me.authToken || isError || (!isPending && !courseData)) {
    return (
      <div className="flex flex-col items-start gap-3 py-4" role="alert">
        <p>Kurz sa nepodarilo načítať. Úpravy zatiaľ nie je možné uložiť.</p>
        {courseSlug && me.authToken && (
          <Button type="button" onClick={() => refetch()}>
            Skúsiť znova
          </Button>
        )}
      </div>
    );
  }

  if (isPending) return <PortalContentLoader />;

  return (
    <FormProvider {...form}>
      <form
        className={cn('flex w-full items-start justify-start gap-2 py-4', 'ea-create-course-form')}
        onSubmit={handleSubmit}
      >
        <AddCourseStepper
          currentStep={currentStep}
          setCurrentStep={handleSetCurrentStep}
          currentErrorStep={currentErrorStep}
        />
        <div className="flex w-full flex-col gap-4">
          {currentStep === 0 && <AddCourseStep0 edit selectedCategories={courseData?.categories} />}
          <AddCourseControls handleSubmit={handleSubmit} edit />
        </div>
      </form>
    </FormProvider>
  );
};
